import { useState } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner, usePermissions } from "@/hooks/use-permissions";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Trash2, Zap, ClipboardCheck, RefreshCw } from "lucide-react";
import type { TaskRule, Property, Vendor, Member, ServiceFormTemplate } from "@shared/schema";
import { usePageTitle } from "@/hooks/use-page-title";

// Forms-on-tasks Phase 4 — Task Rules is the unified recurrence engine. A
// recurring rule spawns a task for its vendor on a cadence (a form-task when a
// template is attached), or sends a member / phone recipient a tokenized form
// link. This absorbed the retired service-form schedules UI. Event triggers
// (move-in / move-out / checkout) exist in the data model but have no engine
// yet, so the builder is recurring-only until one lands.

const FREQUENCY_LABELS: Record<string, string> = {
  daily: "Daily",
  weekly: "Weekly",
  biweekly: "Every 2 weeks",
  monthly: "Monthly",
  quarterly: "Quarterly",
  biannual: "Bi-Annually",
};

const MONTH_STEPPED = new Set(["quarterly", "biannual"]);

const DAYS_OF_WEEK = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

function cadenceLabel(rule: TaskRule): string {
  const freq = FREQUENCY_LABELS[rule.frequency ?? ""] ?? rule.frequency ?? "";
  const parts = [freq];
  if ((rule.frequency === "weekly" || rule.frequency === "biweekly") && rule.dayOfWeek) {
    parts.push(`on ${rule.dayOfWeek[0].toUpperCase()}${rule.dayOfWeek.slice(1)}`);
  }
  if (rule.frequency === "monthly" && rule.dayOfMonth) {
    parts.push(`on day ${rule.dayOfMonth}`);
  }
  if (MONTH_STEPPED.has(rule.frequency ?? "") && rule.nextRunAt) {
    parts.push(`next ${String(rule.nextRunAt).slice(0, 10)}`);
  }
  if (rule.timeOfDay) parts.push(`at ${rule.timeOfDay}`);
  return parts.join(" ");
}

export default function AppOperationsTaskRules() {
  const queryClient = useQueryClient();
  usePageTitle("Task Rules");
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("maintenance");
  const { toast } = useToast();
  const [addOpen, setAddOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const [name, setName] = useState("");
  const [recipientType, setRecipientType] = useState<"vendor" | "member" | "phone">("vendor");
  const [vendorId, setVendorId] = useState<string>("");
  const [memberId, setMemberId] = useState<string>("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [templateId, setTemplateId] = useState<string>("none");
  const [propertyId, setPropertyId] = useState<string>("none");
  const [frequency, setFrequency] = useState("weekly");
  const [dayOfWeek, setDayOfWeek] = useState("monday");
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [timeOfDay, setTimeOfDay] = useState("09:00");
  // Quarterly / bi-annual rules anchor their cycle on a chosen start date.
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [taskTitle, setTaskTitle] = useState("");
  const [taskPriority, setTaskPriority] = useState("medium");

  const rulesQuery = useQuery<TaskRule[]>({
    queryKey: ["/api/app/operations/task-rules"],
  });

  const propertiesQuery = useQuery<Property[]>({
    queryKey: ["/api/app/properties"],
  });

  const vendorsQuery = useQuery<Vendor[]>({
    queryKey: ["/api/app/operations/vendors"],
    queryFn: async () => {
      const res = await fetch("/api/app/operations/vendors", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch vendors");
      return res.json();
    },
  });

  const membersQuery = useQuery<Member[]>({
    queryKey: ["/api/app/devices/members"],
  });

  const templatesQuery = useQuery<ServiceFormTemplate[]>({
    queryKey: ["/api/app/service-forms/templates"],
  });

  // Task #2909 — new rules default to the account-level timezone (set once in
  // Account Settings), falling back to the platform default.
  const profileQuery = useQuery<{ timezone?: string | null }>({
    queryKey: ["/api/app/profile"],
  });
  const effectiveTimezone = profileQuery.data?.timezone || "America/Chicago";

  const createMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      return apiRequest("POST", "/api/app/operations/task-rules", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/task-rules"] });
      toast({ title: "Rule created" });
      resetForm();
      setAddOpen(false);
    },
    onError: (e: Error) => toast({ title: "Failed to create rule", description: parseErrorMessage(e, "Please check the rule and try again."), variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Record<string, unknown> }) => {
      return apiRequest("PATCH", `/api/app/operations/task-rules/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/task-rules"] });
      toast({ title: "Rule updated" });
    },
    onError: (e: Error) => toast({ title: "Failed to update rule", description: parseErrorMessage(e, "Please check the rule and try again."), variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiRequest("DELETE", `/api/app/operations/task-rules/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/task-rules"] });
      toast({ title: "Rule deleted" });
    },
    onError: (e: Error) => toast({ title: "Failed to delete rule", description: parseErrorMessage(e, "Please try again."), variant: "destructive" }),
  });

  const resetForm = () => {
    setName("");
    setRecipientType("vendor");
    setVendorId("");
    setMemberId("");
    setRecipientPhone("");
    setRecipientName("");
    setTemplateId("none");
    setPropertyId("none");
    setFrequency("weekly");
    setDayOfWeek("monday");
    setDayOfMonth(1);
    setTimeOfDay("09:00");
    setTaskTitle("");
    setTaskPriority("medium");
    setEditingId(null);
  };

  const templateRequired = recipientType !== "vendor";
  const recipientValid =
    recipientType === "vendor" ? !!vendorId
    : recipientType === "member" ? !!memberId && memberId !== "none"
    : recipientPhone.trim().length > 0;
  const formValid =
    !!name.trim() &&
    recipientValid &&
    (!templateRequired || templateId !== "none");

  const handleSubmit = () => {
    if (!formValid) return;
    const data: Record<string, unknown> = {
      name,
      trigger: "recurring",
      filters: {},
      isActive: true,
      formTemplateId: templateId !== "none" ? parseInt(templateId) : null,
      propertyId: propertyId !== "none" ? parseInt(propertyId) : null,
      primaryVendorId: recipientType === "vendor" && vendorId ? parseInt(vendorId) : null,
      memberId: recipientType === "member" && memberId !== "none" ? parseInt(memberId) : null,
      recipientPhone: recipientType === "phone" ? recipientPhone.trim() : null,
      recipientName: recipientType !== "vendor" && recipientName.trim() ? recipientName.trim() : null,
      frequency,
      dayOfWeek: frequency === "weekly" || frequency === "biweekly" ? dayOfWeek : null,
      dayOfMonth: frequency === "monthly" ? dayOfMonth : null,
      ...(MONTH_STEPPED.has(frequency) ? { startDate } : {}),
      timeOfDay,
      timezone: effectiveTimezone,
      taskDefaults: {
        ...(taskTitle.trim() ? { title: taskTitle.trim() } : {}),
        priority: taskPriority,
      },
    };
    if (editingId) {
      updateMutation.mutate({ id: editingId, data });
      resetForm();
      setAddOpen(false);
    } else {
      createMutation.mutate(data);
    }
  };

  const openEdit = (rule: TaskRule) => {
    setEditingId(rule.id);
    setName(rule.name);
    setRecipientType(rule.primaryVendorId ? "vendor" : rule.memberId ? "member" : "phone");
    setVendorId(rule.primaryVendorId ? String(rule.primaryVendorId) : "");
    setMemberId(rule.memberId ? String(rule.memberId) : "none");
    setRecipientPhone(rule.recipientPhone ?? "");
    setRecipientName(rule.recipientName ?? "");
    setTemplateId(rule.formTemplateId ? String(rule.formTemplateId) : "none");
    setPropertyId(rule.propertyId ? String(rule.propertyId) : "none");
    setFrequency(rule.frequency ?? "weekly");
    setDayOfWeek(rule.dayOfWeek ?? "monday");
    setDayOfMonth(rule.dayOfMonth ?? 1);
    setTimeOfDay(rule.timeOfDay ?? "09:00");
    setStartDate(rule.nextRunAt ? String(rule.nextRunAt).slice(0, 10) : new Date().toISOString().slice(0, 10));
    const defaults = (rule.taskDefaults ?? {}) as { title?: string; priority?: string };
    setTaskTitle(defaults.title ?? "");
    setTaskPriority(defaults.priority ?? "medium");
    setAddOpen(true);
  };

  const vendors = vendorsQuery.data || [];
  const members = (membersQuery.data || []).filter((m) => m.status === "active");
  const templates = templatesQuery.data || [];
  const properties = propertiesQuery.data || [];
  const rules = rulesQuery.data || [];
  const busy = createMutation.isPending || updateMutation.isPending;

  return (
    <AppLayout>
      <div className="p-6 max-w-4xl mx-auto" data-testid="page-task-rules">
        <ViewOnlyBanner area="maintenance" />
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900" data-testid="text-page-title" data-tour="operations-task-rules-heading">Task Rules</h1>
            <p className="text-sm text-gray-500 mt-1">Spawn recurring tasks and form sends automatically on a schedule</p>
          </div>
          <div className="flex items-center gap-2">
            <Dialog open={addOpen} onOpenChange={(v) => { if (busy) return; setAddOpen(v); if (!v) resetForm(); }}>
              <DialogTrigger asChild>
                <Button disabled={readOnly} data-testid="button-add-rule" data-tour="operations-task-rules-add">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Rule
                </Button>
              </DialogTrigger>
            <DialogContent
              className="max-w-md max-h-[85vh] overflow-y-auto"
              onEscapeKeyDown={(e) => { if (busy) e.preventDefault(); }}
              onInteractOutside={(e) => { if (busy) e.preventDefault(); }}
              onPointerDownOutside={(e) => { if (busy) e.preventDefault(); }}
            >
              <DialogHeader>
                <DialogTitle>{editingId ? "Edit Rule" : "Create Recurring Rule"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Rule Name</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Weekly common-area cleaning" data-testid="input-rule-name" />
                </div>

                <div>
                  <Label>Send To</Label>
                  <Select value={recipientType} onValueChange={(v) => setRecipientType(v as typeof recipientType)}>
                    <SelectTrigger data-testid="select-recipient-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vendor">Vendor (creates a task)</SelectItem>
                      <SelectItem value="member">Member (sends a form link)</SelectItem>
                      <SelectItem value="phone">Phone number (sends a form link)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {recipientType === "vendor" && (
                  <div>
                    <Label>Vendor <span className="text-red-500">*</span></Label>
                    <Select value={vendorId} onValueChange={setVendorId}>
                      <SelectTrigger data-testid="select-primary-vendor">
                        <SelectValue placeholder="Select a vendor..." />
                      </SelectTrigger>
                      <SelectContent>
                        {vendors.map((v) => (
                          <SelectItem key={v.id} value={String(v.id)}>{v.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {recipientType === "member" && (
                  <div>
                    <Label>Member <span className="text-red-500">*</span></Label>
                    <Select value={memberId} onValueChange={setMemberId}>
                      <SelectTrigger data-testid="select-rule-member">
                        <SelectValue placeholder="Select a member..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Select a member...</SelectItem>
                        {members.map((m) => (
                          <SelectItem key={m.id} value={String(m.id)}>{m.name || m.email || `Member ${m.id}`}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {recipientType === "phone" && (
                  <div className="space-y-2">
                    <div>
                      <Label>Phone <span className="text-red-500">*</span></Label>
                      <Input value={recipientPhone} onChange={(e) => setRecipientPhone(e.target.value)} placeholder="+1234567890" data-testid="input-rule-phone" />
                    </div>
                    <div>
                      <Label>Recipient Name</Label>
                      <Input value={recipientName} onChange={(e) => setRecipientName(e.target.value)} placeholder="Recipient name" data-testid="input-rule-recipient-name" />
                    </div>
                  </div>
                )}

                <div>
                  <Label>Form Template{templateRequired ? <span className="text-red-500"> *</span> : " (optional)"}</Label>
                  <p className="text-xs text-gray-500 mb-1">
                    {recipientType === "vendor"
                      ? "Attach a form and the spawned task becomes a fillable form-task."
                      : "Members and phone recipients receive a link to this form."}
                  </p>
                  <Select value={templateId} onValueChange={setTemplateId}>
                    <SelectTrigger data-testid="select-rule-template">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{recipientType === "vendor" ? "No form — plain task" : "Select a template..."}</SelectItem>
                      {templates.map((t) => (
                        <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Property (optional)</Label>
                  <Select value={propertyId} onValueChange={setPropertyId}>
                    <SelectTrigger data-testid="select-rule-property">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-60 overflow-y-auto">
                      <SelectItem value="none">No specific property</SelectItem>
                      {properties.map((p) => (
                        <SelectItem key={p.id} value={String(p.id)}>{p.name || p.address || `Property ${p.id}`}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Frequency</Label>
                    <Select value={frequency} onValueChange={setFrequency}>
                      <SelectTrigger data-testid="select-rule-frequency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(FREQUENCY_LABELS).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {(frequency === "weekly" || frequency === "biweekly") && (
                    <div>
                      <Label>Day of Week</Label>
                      <Select value={dayOfWeek} onValueChange={setDayOfWeek}>
                        <SelectTrigger data-testid="select-rule-day">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DAYS_OF_WEEK.map((d) => (
                            <SelectItem key={d} value={d}>{d[0].toUpperCase()}{d.slice(1)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {MONTH_STEPPED.has(frequency) && (
                    <div>
                      <Label>Start date</Label>
                      <Input
                        type="date"
                        value={startDate}
                        onChange={(e) => { if (e.target.value) setStartDate(e.target.value); }}
                        data-testid="input-rule-start-date"
                      />
                      <p className="text-[11px] text-gray-400 mt-1">
                        First run on this date, then every {frequency === "quarterly" ? "3" : "6"} months.
                      </p>
                    </div>
                  )}
                  {frequency === "monthly" && (
                    <div>
                      <Label>Day of Month</Label>
                      <Select value={String(dayOfMonth)} onValueChange={(v) => setDayOfMonth(parseInt(v))}>
                        <SelectTrigger data-testid="select-rule-dom">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-60 overflow-y-auto">
                          {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                            <SelectItem key={d} value={String(d)}>{d}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                <div>
                  <Label>Time of Day</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="time"
                      value={timeOfDay}
                      onChange={(e) => setTimeOfDay(e.target.value)}
                      className="w-[140px]"
                      data-testid="input-rule-time-of-day"
                    />
                    <span className="text-xs text-gray-500" data-testid="text-rule-timezone">{effectiveTimezone}</span>
                  </div>
                </div>

                {recipientType === "vendor" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Task Title (optional)</Label>
                      <Input value={taskTitle} onChange={(e) => setTaskTitle(e.target.value)} placeholder="Defaults to the form/rule name" data-testid="input-rule-task-title" />
                    </div>
                    <div>
                      <Label>Priority</Label>
                      <Select value={taskPriority} onValueChange={setTaskPriority}>
                        <SelectTrigger data-testid="select-rule-priority">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="urgent">Urgent</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="low">Low</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                <Button onClick={handleSubmit} className="w-full" disabled={readOnly || !formValid || busy} data-testid="button-submit-rule">
                  {busy ? "Saving..." : editingId ? "Update Rule" : "Create Rule"}
                </Button>
              </div>
            </DialogContent>
            </Dialog>
            <div className="flex items-center gap-2">
              <ExplainerVideoButton pageKey="operations-task-rules" />
              <GuideButton pageKey="operations-task-rules" />
            </div>
          </div>
          <GuideNudgeBanner pageKey="operations-task-rules" />
        </div>

        {rulesQuery.isLoading && (
          <div className="space-y-3">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-24 w-full" />)}
          </div>
        )}

        {rules.length === 0 && !rulesQuery.isLoading && (
          <Card>
            <CardContent className="py-12 text-center">
              <Zap className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No rules yet</p>
              <p className="text-sm text-gray-400 mt-1">Create a recurring rule to spawn tasks or send forms automatically</p>
            </CardContent>
          </Card>
        )}

        <div className="space-y-3">
          {rules.map((rule) => {
            const template = rule.formTemplateId ? templates.find((t) => t.id === rule.formTemplateId) : null;
            const vendor = rule.primaryVendorId ? vendors.find((v) => v.id === rule.primaryVendorId) : null;
            const member = rule.memberId ? members.find((m) => m.id === rule.memberId) : null;
            const recipient = vendor?.name
              || member?.name
              || (rule.memberId ? `Member #${rule.memberId}` : null)
              || rule.recipientName
              || rule.recipientPhone
              || "—";
            const RowIcon = template ? ClipboardCheck : RefreshCw;
            return (
              <Card key={rule.id} data-testid={`card-rule-${rule.id}`} data-tour="operations-task-rules-list">
                <CardContent className="py-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-lg bg-brand-muted flex items-center justify-center shrink-0">
                        <RowIcon className="h-5 w-5 text-brand" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-gray-900 break-words">{rule.name}</span>
                          <Badge variant={rule.isActive ? "default" : "secondary"}>
                            {rule.isActive ? "Active" : "Paused"}
                          </Badge>
                          {template && (
                            <Badge variant="secondary" className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                              <ClipboardCheck className="h-3 w-3 mr-1" />
                              {template.name}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 break-words" data-testid={`text-rule-summary-${rule.id}`}>
                          {cadenceLabel(rule)} · {vendor ? `Vendor: ${recipient}` : `To: ${recipient}`}
                          {rule.nextRunAt ? ` · Next: ${new Date(rule.nextRunAt).toLocaleString()}` : ""}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        disabled={readOnly}
                        checked={rule.isActive ?? true}
                        onCheckedChange={(checked) => updateMutation.mutate({ id: rule.id, data: { isActive: checked } })}
                        data-testid={`switch-active-${rule.id}`}
                      />
                      <Button disabled={readOnly}
                        variant="ghost"
                        size="sm"
                        onClick={() => openEdit(rule)}
                        data-testid={`button-edit-rule-${rule.id}`}
                      >
                        Edit
                      </Button>
                      <Button disabled={readOnly}
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700"
                        onClick={() => deleteMutation.mutate(rule.id)}
                        data-testid={`button-delete-rule-${rule.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </AppLayout>
  );
}
